import axios from 'axios'
import type { ApiErrorBody } from '@/types/api'
import { useUiStore } from '@/store/uiStore'
import { parseApiErrors } from '@/utils/apiErrorMapper'
import { log } from '@/utils/logger'
import { getMessage } from '@/utils/messages'

/**
 * Central handler for all non-2xx API responses.
 *
 * - Parses the standardized ApiErrorBody from the response
 * - Shows a toast with the human-readable message
 * - Maps field-level errors to form inputs via setApiFieldErrors
 * - Logs traceId + timestamp for debugging
 * - Handles 429 Retry-After by storing the retry timestamp in uiStore
 */
export function handleApiError(error: unknown): void {
  const { showToast, setApiFieldErrors, setRateLimitedUntil } = useUiStore.getState()

  // Plain Error thrown from application code (e.g. mutationFn guard checks)
  if (!axios.isAxiosError(error)) {
    const msg = error instanceof Error ? error.message : getMessage('api.error.unexpected')
    log.error(msg, error instanceof Error ? error : undefined)
    showToast(msg, 'error')
    return
  }

  // Network / no-response error (AxiosError with no HTTP response)
  if (!error.response) {
    log.error('Network error — no response received', error)
    showToast(getMessage('api.error.network'), 'error')
    return
  }

  const body = error.response.data as Partial<ApiErrorBody>
  const statusCode = error.response.status
  const message = body.message ?? getMessage('api.error.unexpected')
  const { traceId, timestamp, path, code } = body

  // Always log trace info for debugging / App Insights correlation
  log.error(`[API ${statusCode}] ${code ?? 'UNKNOWN'}: ${message}`, undefined, {
    traceId,
    timestamp,
    path,
    statusCode: String(statusCode),
    code,
  })

  // 429 — rate limited: disable the triggering action until Retry-After elapses
  if (statusCode === 429) {
    const retryAfterHeader = error.response.headers['retry-after']
    const seconds = retryAfterHeader ? parseInt(retryAfterHeader, 10) : 30
    setRateLimitedUntil(Date.now() + seconds * 1000)
    // Auto-clear once the window expires
    setTimeout(() => setRateLimitedUntil(null), seconds * 1000)
    showToast(
      getMessage('api.error.rate_limited', {
        seconds: String(seconds),
        plural: seconds !== 1 ? 's' : '',
      }),
      'warning'
    )
    return
  }

  // 5xx — server error: show traceId so users can quote it to support
  if (statusCode >= 500) {
    const ref = traceId ? ` (ref: ${traceId})` : ''
    showToast(getMessage('api.error.server_error', { ref }), 'error')
    return
  }

  // 413 — payload too large
  if (statusCode === 413) {
    showToast(getMessage('api.error.payload_too_large'), 'warning')
    return
  }

  // 400 INVALID_JSON
  if (code === 'INVALID_JSON') {
    showToast(getMessage('api.error.invalid_json'), 'error')
    return
  }

  // Field-level validation errors — map to form inputs
  if (body.errors?.length) {
    setApiFieldErrors(parseApiErrors(body.errors))
  }

  // All other AppError codes — display the API message directly
  showToast(message, 'error')
}
